import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material';
import {
  Alert,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import { Client } from '../../types/client';
import { Property } from '../../types/property.types';
import revoLogo from '../../assets/revo.png';

// Import modular components
import CompanyDetailsSection from './CompanyDetailsSection';
import TradingDurationSection from './TradingDurationSection';
import DocumentViewerSection from './DocumentViewerSection';
import SitePropertiesSection from './SitePropertiesSection';
import AcceptDeclineSection from './AcceptDeclineSection';

// Helper: Convert years + months to total months
const toMonths = (y?: string | number, m?: string | number) =>
  (parseInt(String(y || 0), 10) * 12) + (parseInt(String(m || 0), 10) || 0);

// Helper: Check if trading duration requires additional info (< 3 years = < 36 months)
const needsExtraInfoFromDuration = (years: number | string, months: number | string) =>
  toMonths(years, months) < 36;

// Helper: Exact 18+ age check
const isAtLeast18 = (dob: Date | null) => {
  if (!dob) return false;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age >= 18;
};

// Helper: Check for overlapping dates in address history
const checkForOverlaps = (
  homeAddress: any,
  addressHistory: Array<{ movedInDate: string; movedOutDate?: string }>
): { hasOverlaps: boolean; overlapMessage?: string } => {
  const addresses = [...addressHistory];
  
  // Add current address to the check
  if (homeAddress?.movedInDate) {
    addresses.push({
      movedInDate: homeAddress.movedInDate,
      movedOutDate: undefined // Current address has no end date
    });
  }
  
  // Sort by movedInDate
  const sorted = addresses
    .filter(addr => addr.movedInDate)
    .sort((a, b) => new Date(a.movedInDate).getTime() - new Date(b.movedInDate).getTime());
  
  // Check for overlaps
  for (let i = 0; i < sorted.length - 1; i++) {
    const current = sorted[i];
    const next = sorted[i + 1];
    
    const currentEnd = current.movedOutDate ? new Date(current.movedOutDate) : new Date();
    const nextStart = new Date(next.movedInDate);
    
    if (currentEnd > nextStart) {
      return {
        hasOverlaps: true,
        overlapMessage: `Address dates overlap: one address ends after the next one begins`
      };
    }
  }
  
  return { hasOverlaps: false };
};

// Helper: Calculate address history coverage in months
const calculateAddressCoverage = (
  homeAddress: any,
  addressHistory: Array<{ movedInDate: string; movedOutDate?: string }>
): { totalMonths: number; hasGaps: boolean; coverageText: string } => {
  const today = new Date();
  const fiveYearsAgo = new Date();
  fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
  
  // Start with current address
  const currentMovedIn = homeAddress?.movedInDate ? new Date(homeAddress.movedInDate) : null;
  
  if (!currentMovedIn) {
    return { totalMonths: 0, hasGaps: true, coverageText: 'No move-in date provided' };
  }
  
  // Calculate months from current address
  const currentMonths = Math.floor((today.getTime() - currentMovedIn.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
  
  // If current address covers 5+ years, we're done
  if (currentMonths >= 60) {
    const years = Math.floor(currentMonths / 12);
    const months = currentMonths % 12;
    return { 
      totalMonths: currentMonths, 
      hasGaps: false, 
      coverageText: `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''} (sufficient)` 
    };
  }
  
  // Need to check address history
  let totalMonths = currentMonths;
  let oldestDate = currentMovedIn;
  
  // Sort address history by movedOutDate (most recent first)
  const sortedHistory = [...(addressHistory || [])].sort((a, b) => {
    const dateA = a.movedOutDate ? new Date(a.movedOutDate).getTime() : 0;
    const dateB = b.movedOutDate ? new Date(b.movedOutDate).getTime() : 0;
    return dateB - dateA;
  });
  
  for (const addr of sortedHistory) {
    if (!addr.movedInDate) continue;
    
    const movedIn = new Date(addr.movedInDate);
    const movedOut = addr.movedOutDate ? new Date(addr.movedOutDate) : oldestDate;
    
    // Calculate months for this address
    const addressMonths = Math.floor((movedOut.getTime() - movedIn.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
    
    if (addressMonths > 0) {
      totalMonths += addressMonths;
      oldestDate = movedIn;
    }
    
    // If we've reached 5 years, stop
    if (totalMonths >= 60) break;
  }
  
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  const hasGaps = totalMonths < 60;
  
  if (hasGaps) {
    const neededMonths = 60 - totalMonths;
    const neededYears = Math.floor(neededMonths / 12);
    const neededRemainder = neededMonths % 12;
    return {
      totalMonths,
      hasGaps: true,
      coverageText: `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''} (need ${neededYears} year${neededYears !== 1 ? 's' : ''} ${neededRemainder} month${neededRemainder !== 1 ? 's' : ''} more)`
    };
  }
  
  return {
    totalMonths,
    hasGaps: false,
    coverageText: `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''} (sufficient)`
  };
};

function LOAPage() {
  const { token } = useParams<{ token: string }>();
  const theme = useTheme();

  // Define section style for consistent UI across components
  const sectionStyle = {
    background: `linear-gradient(135deg, ${alpha('#1976d2', 0.02)} 0%, ${alpha('#42a5f5', 0.04)} 100%)`,
    borderRadius: '16px',
    padding: theme.spacing(4),
    border: `1px solid ${alpha('#1976d2', 0.12)}`,
    position: 'relative',
    overflow: 'hidden',
    color: '#1a1a1a',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-2px)'
    },
    '& .MuiTypography-root': {
      color: '#1a1a1a'
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '5px',
      height: '100%',
      background: 'linear-gradient(180deg, #1976d2 0%, #42a5f5 100%)',
      borderRadius: '16px 0 0 16px'
    }
  };
  
  // Client and PDF data
  const [client, setClient] = useState<Client | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [pdfUrl, setPdfUrl] = useState('');
  
  // UI state
  const [hasViewedLOA, setHasViewedLOA] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdditionalInfoDialog, setShowAdditionalInfoDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [declined, setDeclined] = useState(false);
  
  // Form data
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
  const [name, setName] = useState<string>('');
  const [position, setPosition] = useState<string>('');
  const [signatureConfirmed, setSignatureConfirmed] = useState<boolean>(false);
  const [tradingYears, setTradingYears] = useState<string>('');
  const [tradingMonths, setTradingMonths] = useState<string>('');
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  
  // Home Address Fields
  const [homeAddressLine1, setHomeAddressLine1] = useState('');
  const [homeAddressLine2, setHomeAddressLine2] = useState('');
  const [homeCity, setHomeCity] = useState('');
  const [homeCounty, setHomeCounty] = useState('');
  const [homePostcode, setHomePostcode] = useState('');
  const [homeMovedInDate, setHomeMovedInDate] = useState<Date | null>(null);
  
  // Address History
  const [addressHistory, setAddressHistory] = useState<Array<{
    address: {
      line1: string;
      line2?: string;
      city: string;
      county?: string;
      postcode: string;
    };
    movedInDate: string;
    movedOutDate?: string;
    isAbroad?: boolean;
  }>>([]);
  
  // Address coverage tracking
  const [addressCoverage, setAddressCoverage] = useState<{
    totalMonths: number;
    hasGaps: boolean;
    coverageText: string;
  } | null>(null);
  
  // Address overlap tracking
  const [addressOverlap, setAddressOverlap] = useState<{
    hasOverlaps: boolean;
    overlapMessage?: string;
  } | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!token) {
        setError('No LOA token provided');
        setLoading(false);
        return;
      }

      // Fetch client data using the LOA token
      console.log('Fetching client data with token:', token);
      const clientResponse = await axios.get(`/api/loa/public/view/${token}`);
      console.log('Client response received:', clientResponse.data);
      
      if (!clientResponse.data.success) {
        console.error('Client data fetch failed:', clientResponse.data.message);
        setError(clientResponse.data.message || 'Failed to load LOA data');
        setLoading(false);
        return;
      }

      const clientData = clientResponse.data.client;
      if (!clientData) {
        console.error('Client data not found in response. Full response:', JSON.stringify(clientResponse.data));
        setError('Client data not found in response');
        setLoading(false);
        return;
      }
      console.log('Client data successfully loaded:', clientData);
      setClient(clientData);
      
      // Set properties if available in the client data
      if (clientData.properties && Array.isArray(clientData.properties)) {
        console.log('Properties found:', clientData.properties);
        setProperties(clientData.properties);
      } else {
        console.log('No properties found in client data');
      }
      
      // Set name and position from client data
      if (clientData.ownerName) {
        setName(clientData.ownerName);
      }
      if (clientData.position) {
        setPosition(clientData.position);
      }
      
      // Pre-populate form fields from client data
      if (clientData.dateOfBirth) {
        const dob = new Date(clientData.dateOfBirth);
        if (!isNaN(dob.getTime())) {
          setDateOfBirth(dob);
        }
      }
      if (clientData.homeAddress) {
        setHomeAddressLine1(clientData.homeAddress.line1 || '');
        setHomeAddressLine2(clientData.homeAddress.line2 || '');
        setHomeCity(clientData.homeAddress.city || '');
        setHomeCounty(clientData.homeAddress.county || '');
        setHomePostcode(clientData.homeAddress.postcode || '');
        if (clientData.homeAddress.movedInDate) {
          const movedIn = new Date(clientData.homeAddress.movedInDate);
          if (!isNaN(movedIn.getTime())) {
            setHomeMovedInDate(movedIn);
          }
        }
      }
      if (clientData.addressHistory && clientData.addressHistory.length > 0) {
        setAddressHistory(clientData.addressHistory);
      }
      
      // Restore form data from localStorage if available (overrides client data)
      const savedFormData = localStorage.getItem(`loa_form_${token}`);
      if (savedFormData) {
        try {
          const parsedData = JSON.parse(savedFormData);
          if (parsedData.dateOfBirth) {
            const dob = new Date(parsedData.dateOfBirth);
            if (!isNaN(dob.getTime())) {
              setDateOfBirth(dob);
            }
          }
          setTradingYears(parsedData.tradingYears || '');
          setTradingMonths(parsedData.tradingMonths || '');
          if (parsedData.homeAddressLine1) setHomeAddressLine1(parsedData.homeAddressLine1);
          if (parsedData.homeAddressLine2) setHomeAddressLine2(parsedData.homeAddressLine2);
          if (parsedData.homeCity) setHomeCity(parsedData.homeCity);
          if (parsedData.homeCounty) setHomeCounty(parsedData.homeCounty);
          if (parsedData.homePostcode) setHomePostcode(parsedData.homePostcode);
          if (parsedData.homeMovedInDate) {
            const movedIn = new Date(parsedData.homeMovedInDate);
            if (!isNaN(movedIn.getTime())) {
              setHomeMovedInDate(movedIn);
            }
          }
          if (parsedData.addressHistory) setAddressHistory(parsedData.addressHistory);
        } catch (e) {
          console.error('Error parsing saved form data:', e);
          // Continue without saved data
        }
      }
      
      // Try to get the PDF URL - always fetch fresh presigned URL
      try {
        // First get the client ID from the client data
        if (clientData && clientData._id) {
          console.log('Fetching fresh presigned URL with client ID:', clientData._id);
          
          // Always fetch from the presigned URL endpoint to ensure fresh URL
          const pdfResponse = await axios.get(`/api/loa/public/get-presigned-url/${clientData._id}`);
          console.log('PDF URL response:', pdfResponse.data);
          
          if (pdfResponse.data.success && pdfResponse.data.url) {
            console.log('Successfully got fresh presigned URL:', pdfResponse.data.url);
            setPdfUrl(pdfResponse.data.url);
          } else {
            console.error('Failed to get PDF URL:', pdfResponse.data);
          }
        } else {
          console.error('Client ID not available to fetch PDF URL. Client data:', clientData);
        }
      } catch (pdfError) {
        console.error('Error getting PDF URL:', pdfError);
      }
      
      // Calculate showAdditionalInfo based on business type
      let showAdditional = false;
      
      if (clientData.businessType === 'Sole Trader') {
        // For Sole Traders, check yearsTrading and monthsTrading
        const years = clientData.yearsTrading || 0;
        const months = clientData.monthsTrading || 0;
        const totalMonths = (years * 12) + months;
        showAdditional = totalMonths < 36; // Less than 3 years (36 months)
        
        console.log('Sole Trader trading duration:', { years, months, totalMonths, showAdditional });
      } else if (clientData.incorporationDate) {
        // For Limited Companies and Charities, check incorporation date
        const incorporationDate = new Date(clientData.incorporationDate);
        
        // Guard against invalid dates
        if (!isNaN(incorporationDate.getTime())) {
          const today = new Date();
          const yearsDiff = today.getFullYear() - incorporationDate.getFullYear();
          const monthsDiff = today.getMonth() - incorporationDate.getMonth();
          const daysDiff = today.getDate() - incorporationDate.getDate();
          
          // Calculate if less than 3 years
          let yearsIncorporated = yearsDiff;
          if (monthsDiff < 0 || (monthsDiff === 0 && daysDiff < 0)) {
            yearsIncorporated--;
          }
          
          showAdditional = yearsIncorporated < 3;
          console.log('Company incorporation duration:', { yearsIncorporated, showAdditional });
        } else {
          console.warn('Invalid incorporation date:', clientData.incorporationDate);
        }
      }
      
      // Show additional info based on business type and trading duration
      // 1. Sole Traders: ALWAYS need home address with movedInDate + 5-year address history
      // 2. Limited Company/Charity < 3 years: Need director/trustee information
      const hasDateOfBirth = !!clientData.dateOfBirth;
      const hasHomeAddress = !!clientData.homeAddress?.line1;
      const hasMovedInDate = !!clientData.homeAddress?.movedInDate;
      const hasAddressHistory = clientData.addressHistory && clientData.addressHistory.length > 0;
      
      if (clientData.businessType === 'Sole Trader') {
        // For Sole Traders, only show if missing required data
        const needsInfo = !hasDateOfBirth || !hasHomeAddress || !hasMovedInDate;
        setShowAdditionalInfo(needsInfo);
        console.log('Sole Trader - checking if additional info needed:', { 
          hasDateOfBirth, 
          hasHomeAddress, 
          hasMovedInDate,
          hasAddressHistory,
          needsInfo
        });
      } else if (showAdditional) {
        // For Limited Company/Charity trading < 3 years, only show if missing required data
        const needsInfo = !hasDateOfBirth || !hasHomeAddress || !hasMovedInDate;
        setShowAdditionalInfo(needsInfo);
        console.log('Company/Charity < 3 years - checking if additional info needed:', { 
          businessType: clientData.businessType,
          showAdditional,
          hasDateOfBirth, 
          hasHomeAddress,
          hasMovedInDate,
          needsInfo
        });
      } else {
        setShowAdditionalInfo(false);
      }
      
      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching LOA data:', error);
      setError(error.response?.data?.message || 'Failed to load LOA');
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Persist form data to localStorage
  useEffect(() => {
    if (!token) return;
    const data = {
      dateOfBirth: (dateOfBirth && !isNaN(dateOfBirth.getTime())) ? dateOfBirth.toISOString() : null,
      tradingYears,
      tradingMonths,
      homeAddressLine1,
      homeAddressLine2,
      homeCity,
      homeCounty,
      homePostcode,
      homeMovedInDate: (homeMovedInDate && !isNaN(homeMovedInDate.getTime())) ? homeMovedInDate.toISOString() : null,
      addressHistory,
    };
    localStorage.setItem(`loa_form_${token}`, JSON.stringify(data));
  }, [
    token,
    dateOfBirth,
    tradingYears,
    tradingMonths,
    homeAddressLine1,
    homeAddressLine2,
    homeCity,
    homeCounty,
    homePostcode,
    homeMovedInDate,
    addressHistory,
  ]);

  // Calculate address coverage and check for overlaps whenever address data changes
  useEffect(() => {
    if (showAdditionalInfo && (client?.homeAddress || homeAddressLine1)) {
      // Use the current form state for home address, not the client data
      const currentHomeAddress = {
        line1: homeAddressLine1 || client?.homeAddress?.line1,
        line2: homeAddressLine2 || client?.homeAddress?.line2,
        city: homeCity || client?.homeAddress?.city,
        county: homeCounty || client?.homeAddress?.county,
        postcode: homePostcode || client?.homeAddress?.postcode,
        movedInDate: homeMovedInDate ? homeMovedInDate.toISOString() : client?.homeAddress?.movedInDate
      };
      
      const coverage = calculateAddressCoverage(currentHomeAddress, addressHistory);
      setAddressCoverage(coverage);
      
      const overlap = checkForOverlaps(currentHomeAddress, addressHistory);
      setAddressOverlap(overlap);
    } else {
      setAddressCoverage(null);
      setAddressOverlap(null);
    }
  }, [showAdditionalInfo, client?.homeAddress, homeAddressLine1, homeAddressLine2, homeCity, homeCounty, homePostcode, homeMovedInDate, addressHistory]);

  // Refresh presigned URL every 45 minutes (before the 1-hour expiration)
  useEffect(() => {
    if (!client || !client._id) return;

    const refreshPresignedUrl = async () => {
      try {
        console.log('Refreshing presigned URL for LOA page...');
        const response = await axios.get(`/api/loa/public/get-presigned-url/${client._id}`);
        if (response.data.success && response.data.url) {
          console.log('Presigned URL refreshed successfully');
          setPdfUrl(response.data.url);
        }
      } catch (error) {
        console.error('Failed to refresh presigned URL:', error);
      }
    };

    // Refresh immediately if we don't have a URL
    if (!pdfUrl) {
      refreshPresignedUrl();
    }

    // Set up interval to refresh every 45 minutes
    const intervalId = setInterval(refreshPresignedUrl, 45 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [client, pdfUrl]);

  // Handle Accept button click
  const handleAcceptClick = () => {
    // Always show the dialog to collect name, position, and signature confirmation
    // Additional info fields will only be shown if showAdditionalInfo is true
    setShowAdditionalInfoDialog(true);
  };

  // Handle Accept submission (with or without additional info)
  const handleAcceptSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      // Validate required fields
      if (!name.trim()) {
        setError('Please provide your name');
        setSubmitting(false);
        return;
      }

      if (!position.trim()) {
        setError('Position is required. Please contact support if your position is not shown.');
        setSubmitting(false);
        return;
      }

      if (!signatureConfirmed) {
        setError('Please confirm you are authorised to sign this document');
        setSubmitting(false);
        return;
      }

      const payload: any = {
        name: name.trim(),
        position: position.trim(),
        signatureConfirmed: signatureConfirmed
      };

      // Add additional info if required
      if (showAdditionalInfo) {
        if (dateOfBirth) {
          payload.dateOfBirth = dateOfBirth;
        }
        if (homeAddressLine1) {
          payload.homeAddress = {
            line1: homeAddressLine1,
            line2: homeAddressLine2,
            city: homeCity,
            county: homeCounty,
            postcode: homePostcode,
            movedInDate: homeMovedInDate
          };
        }
        if (addressHistory && addressHistory.length > 0) {
          payload.addressHistory = addressHistory;
        }
      }

      const response = await axios.post(`/api/loa/public/accept/${token}`, payload);

      if (response.data.success) {
        // Success! Show success screen
        setAccepted(true);
        setShowAdditionalInfoDialog(false);
      } else {
        setError(response.data.message || 'Failed to accept LOA');
      }
    } catch (error: any) {
      console.error('Error accepting LOA:', error);
      setError(error.response?.data?.message || 'Failed to accept LOA. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Decline submission
  const handleDeclineSubmit = async (reason: string) => {
    try {
      setSubmitting(true);
      setError(null);

      const response = await axios.post(`/api/loa/public/decline/${token}`, {
        reason
      });

      if (response.data.success) {
        // Success! Show decline confirmation
        setDeclined(true);
      } else {
        setError(response.data.message || 'Failed to decline LOA');
      }
    } catch (error: any) {
      console.error('Error declining LOA:', error);
      setError(error.response?.data?.message || 'Failed to decline LOA. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading state while fetching data
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)'
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3, color: '#1a1a1a' }}>
          Loading Letter of Authority...
        </Typography>
      </Box>
    );
  }

  // If the LOA has been accepted or declined, show a confirmation screen
  if (accepted || declined) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        padding: { xs: 2, sm: 3 },
        textAlign: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)'
      }}>
        {accepted ? (
          <>
            <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ color: '#1a1a1a', fontWeight: 600 }}>
              Thank You!
            </Typography>
            <Typography variant="body1" paragraph sx={{ color: '#333', maxWidth: 600 }}>
              You have successfully accepted the Letter of Authority. Our team will process your request shortly.
            </Typography>
          </>
        ) : (
          <>
            <CancelIcon color="error" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ color: '#1a1a1a', fontWeight: 600 }}>
              LOA Declined
            </Typography>
            <Typography variant="body1" paragraph sx={{ color: '#333', maxWidth: 600 }}>
              You have declined the Letter of Authority. If you have any questions, please contact our support team.
            </Typography>
          </>
        )}
        <Button 
          variant="contained" 
          onClick={() => window.location.href = 'https://revo-utilities.com'}
          sx={{ mt: 2 }}
        >
          Close Window
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)',
      py: { xs: 3, sm: 5 }, 
      px: { xs: 1, sm: 2 } 
    }}>
      <Paper elevation={3} sx={{ 
        maxWidth: 1000, 
        mx: 'auto', 
        p: { xs: 3, sm: 4, md: 5 },
        overflowX: 'hidden',
        overflowY: 'auto',
        backgroundColor: '#ffffff',
        color: '#1a1a1a',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        '& .MuiTypography-root': {
          color: '#1a1a1a'
        }
      }}>
        <Box sx={{ 
          width: '100%',
          boxSizing: 'border-box',
        }} position="relative">
          {/* Logo with responsive positioning */}
          <Box 
            sx={{ 
              position: { xs: 'relative', sm: 'absolute' },
              top: { sm: 24 },
              right: { sm: 24 },
              width: { xs: '120px', sm: '150px' },
              mb: { xs: 2, sm: 0 },
              display: 'flex',
              justifyContent: { xs: 'center', sm: 'flex-end' },
              mx: { xs: 'auto', sm: 0 }
            }}
          >
            <img 
              src={revoLogo} 
              alt="REVO Utilities" 
              style={{ width: '100%', height: 'auto' }} 
            />
          </Box>

          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              mb: 4, 
              mt: { xs: 2, sm: 3 },
              fontSize: { xs: '1.75rem', sm: '2rem' },
              fontWeight: 700,
              textAlign: { xs: 'center', sm: 'left' },
              wordBreak: 'break-word',
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.5px'
            }}
          >
            Letter of Authority Review
          </Typography>

          {/* Display error message inline with improved mobile styling */}
          {error && (
            <Box 
              mb={4} 
              mt={2}
              sx={{ 
                width: '100%',
                position: 'relative',
                zIndex: 1
              }}
            >
              <Alert 
                severity="error" 
                onClose={() => setError(null)}
                sx={{
                  width: '100%',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  py: { xs: 1.5, sm: 0 },
                  '& .MuiAlert-message': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    width: '100%',
                    mt: { xs: 1, sm: 0 }
                  },
                  '& .MuiAlert-icon': {
                    mt: { xs: 1, sm: 0 },
                    mr: { xs: 1, sm: 1.5 }
                  },
                  '& .MuiAlert-action': {
                    padding: { xs: '4px', sm: '8px' },
                    position: { xs: 'absolute', sm: 'static' },
                    top: { xs: '4px', sm: 'auto' },
                    right: { xs: '4px', sm: 'auto' }
                  }
                }}
                variant="filled"
              >
                {error}
              </Alert>
            </Box>
          )}

          {client && (
            <>
              {/* Company Details Section - Using modular component */}
              <CompanyDetailsSection client={client} sectionStyle={sectionStyle} />

              {/* Site Properties Section - Using modular component */}
              <SitePropertiesSection properties={properties} sectionStyle={sectionStyle} />

              {/* Trading Duration Section - Only for Sole Traders - Using modular component */}
              {/* Hidden - will be shown in dialog when accepting LOA */}
              {client.businessType === 'Sole Trader' && (
                <Box sx={{ display: 'none' }}>
                  <TradingDurationSection 
                    client={client} 
                    tradingYears={tradingYears} 
                    setTradingYears={setTradingYears} 
                    tradingMonths={tradingMonths} 
                    setTradingMonths={setTradingMonths} 
                    calculateShowAdditionalInfo={(years, months) => {
                      const yearsNum = parseInt(years) || 0;
                      const monthsNum = parseInt(months) || 0;
                      const showAdditional = yearsNum < 3 || (yearsNum === 3 && monthsNum === 0);
                      setShowAdditionalInfo(showAdditional);
                      return showAdditional;
                    }}
                    sectionStyle={sectionStyle} 
                  />
                </Box>
              )}

              {/* Document Viewer Section - Using modular component */}
              <DocumentViewerSection 
                pdfUrl={pdfUrl} 
                hasViewedLOA={hasViewedLOA} 
                setHasViewedLOA={setHasViewedLOA} 
                sectionStyle={sectionStyle}
                token={token}
                clientId={client?._id}
                onUrlRefresh={(newUrl) => setPdfUrl(newUrl)}
              />

              {/* Accept/Decline Section - Only show after viewing LOA */}
              {hasViewedLOA && (
                <AcceptDeclineSection 
                  hasViewedLOA={hasViewedLOA}
                  onAccept={handleAcceptClick}
                  onDecline={handleDeclineSubmit}
                  sectionStyle={sectionStyle}
                  disabled={submitting}
                />
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* Additional Info Dialog - Only shown when accepting and additional info is required */}
      <Dialog 
        open={showAdditionalInfoDialog} 
        onClose={() => !submitting && setShowAdditionalInfoDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'white',
            backgroundImage: 'none'
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: '#1976d2' }}>
            LOA Acceptance
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* Signature Details */}
          <Box mb={4} mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Your Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={!name}
                  helperText={!name ? 'Name is required' : ''}
                  sx={{
                    backgroundColor: 'white',
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                      fontWeight: 400
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Position"
                  value={position}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  helperText={position ? "Position from company records" : "No position found in records"}
                  error={!position}
                  sx={{
                    backgroundColor: 'white',
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                      fontWeight: 400
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={signatureConfirmed}
                      onChange={(e) => setSignatureConfirmed(e.target.checked)}
                      color="primary"
                      sx={{ 
                        color: 'rgba(0, 0, 0, 0.6)',
                        '&.Mui-checked': {
                          color: '#1976d2'
                        }
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: 'rgba(0, 0, 0, 0.87)', fontSize: '0.95rem' }}>
                      I confirm that I am authorised to sign this Letter of Authority on behalf of the company
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Box>

          {/* Trading Duration Section - Only for Sole Traders who don't have it yet */}
          {client?.businessType === 'Sole Trader' && !client?.yearsTrading && !client?.monthsTrading && (
            <Box mb={4}>
              <Typography variant="h6" gutterBottom sx={{ color: '#333', fontWeight: 600, mb: 1 }}>
                Trading Duration
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>
                How long has your business been trading?
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Years"
                    type="number"
                    value={tradingYears}
                    onChange={(e) => {
                      setTradingYears(e.target.value);
                      setShowAdditionalInfo(needsExtraInfoFromDuration(e.target.value, tradingMonths));
                    }}
                    inputProps={{ min: 0, max: 100 }}
                    sx={{
                      backgroundColor: 'white',
                      '& .MuiInputBase-input': {
                        color: 'rgba(0, 0, 0, 0.87)',
                        fontWeight: 400
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.6)'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Months"
                    type="number"
                    value={tradingMonths}
                    onChange={(e) => {
                      setTradingMonths(e.target.value);
                      setShowAdditionalInfo(needsExtraInfoFromDuration(tradingYears, e.target.value));
                    }}
                    inputProps={{ min: 0, max: 11 }}
                    sx={{
                      backgroundColor: 'white',
                      '& .MuiInputBase-input': {
                        color: 'rgba(0, 0, 0, 0.87)',
                        fontWeight: 400
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.6)'
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Additional Information Section - Only shown if needed */}
          {showAdditionalInfo && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom sx={{ color: '#333', fontWeight: 600, mb: 2, mt: 3 }}>
                Additional Information Required
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: '#555' }}>
                As your business has been trading for less than 3 years, we need some additional information to complete the Letter of Authority.
              </Typography>

              {/* Date of Birth - Only if not already provided (required for all business types < 3 years) */}
              {!client?.dateOfBirth && (
                <Box mb={4}>
            <Typography variant="h6" gutterBottom sx={{ color: '#333', fontWeight: 600, mb: 1 }}>
              Date of Birth
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>
              Please provide your date of birth for verification purposes:
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date of Birth"
                value={dateOfBirth}
                onChange={(newValue) => setDateOfBirth(newValue)}
                format="dd/MM/yyyy"
                maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 18))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: 'Must be at least 18 years old',
                    error: dateOfBirth ? new Date().getFullYear() - dateOfBirth.getFullYear() < 18 : false,
                    sx: { 
                      maxWidth: 300,
                      backgroundColor: 'rgba(255, 255, 255, 0.9) !important',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9) !important',
                        '& fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.23) !important'
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.87) !important'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main !important'
                        }
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.6) !important'
                      },
                      '& .MuiInputBase-input': {
                        color: 'rgba(0, 0, 0, 0.87) !important'
                      },
                      '& .MuiIconButton-root': {
                        color: 'rgba(0, 0, 0, 0.54)'
                      }
                    }
                  },
                  popper: {
                    sx: {
                      '& .MuiPaper-root': {
                        backgroundColor: 'white',
                        color: 'rgba(0, 0, 0, 0.87)'
                      },
                      '& .MuiPickersCalendarHeader-label': {
                        color: 'rgba(0, 0, 0, 0.87)'
                      },
                      '& .MuiPickersCalendarHeader-switchViewButton': {
                        color: 'rgba(0, 0, 0, 0.54)'
                      },
                      '& .MuiPickersArrowSwitcher-button': {
                        color: 'rgba(0, 0, 0, 0.54)'
                      },
                      '& .MuiDayCalendar-weekDayLabel': {
                        color: 'rgba(0, 0, 0, 0.6)'
                      },
                      '& .MuiPickersDay-root': {
                        color: 'rgba(0, 0, 0, 0.87)'
                      },
                      '& .MuiPickersYear-yearButton': {
                        color: 'rgba(0, 0, 0, 0.87)'
                      }
                    }
                  }
                }}
              />
            </LocalizationProvider>
                </Box>
              )}

              {/* Home Address - Only if not already provided (required for all business types < 3 years) */}
              {!client?.homeAddress?.line1 && (
                <Box>
            <Typography variant="h6" gutterBottom sx={{ color: '#333', fontWeight: 600, mb: 1 }}>
              Home Address
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: '#555' }}>
              Please provide your home address details:
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={homeAddressLine1}
                  onChange={(e) => setHomeAddressLine1(e.target.value)}
                  required
                  sx={{
                    backgroundColor: 'white',
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                      fontWeight: 400
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address Line 2 (Optional)"
                  value={homeAddressLine2}
                  onChange={(e) => setHomeAddressLine2(e.target.value)}
                  sx={{
                    backgroundColor: 'white',
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                      fontWeight: 400
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City/Town"
                  value={homeCity}
                  onChange={(e) => setHomeCity(e.target.value)}
                  required
                  sx={{
                    backgroundColor: 'white',
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                      fontWeight: 400
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="County (Optional)"
                  value={homeCounty}
                  onChange={(e) => setHomeCounty(e.target.value)}
                  sx={{
                    backgroundColor: 'white',
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                      fontWeight: 400
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Postcode"
                  value={homePostcode}
                  onChange={(e) => setHomePostcode(e.target.value.toUpperCase())}
                  required
                  sx={{
                    backgroundColor: 'white',
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                      fontWeight: 400
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Moved In Date"
                    value={homeMovedInDate}
                    onChange={(newValue) => setHomeMovedInDate(newValue)}
                    format="dd/MM/yyyy"
                    maxDate={new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        helperText: 'When did you move to this address?',
                        sx: {
                          backgroundColor: 'white',
                          '& .MuiInputBase-input': {
                            color: 'rgba(0, 0, 0, 0.87)',
                            fontWeight: 400
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(0, 0, 0, 0.6)'
                          },
                          '& .MuiIconButton-root': {
                            color: 'rgba(0, 0, 0, 0.54)'
                          }
                        }
                      },
                      popper: {
                        sx: {
                          '& .MuiPaper-root': {
                            backgroundColor: 'white',
                            color: 'rgba(0, 0, 0, 0.87)'
                          },
                          '& .MuiPickersCalendarHeader-label': {
                            color: 'rgba(0, 0, 0, 0.87)'
                          },
                          '& .MuiPickersCalendarHeader-switchViewButton': {
                            color: 'rgba(0, 0, 0, 0.54)'
                          },
                          '& .MuiPickersArrowSwitcher-button': {
                            color: 'rgba(0, 0, 0, 0.54)'
                          },
                          '& .MuiDayCalendar-weekDayLabel': {
                            color: 'rgba(0, 0, 0, 0.6)'
                          },
                          '& .MuiPickersDay-root': {
                            color: 'rgba(0, 0, 0, 0.87)'
                          },
                          '& .MuiPickersYear-yearButton': {
                            color: 'rgba(0, 0, 0, 0.87)'
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
                </Box>
              )}

              {/* Address History - Only shown if moved in less than 5 years ago */}
              {homeMovedInDate && (() => {
                const fiveYearsAgo = new Date();
                fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
                return homeMovedInDate > fiveYearsAgo;
              })() && (
              <Box mt={4} mb={4}>
                <Typography variant="h6" gutterBottom sx={{ color: '#333', fontWeight: 600, mb: 1 }}>
                  Address History (Last 5 Years)
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>
                  We need to verify you have 5 years of address history. If you've lived at your current address for less than 5 years, please provide your previous addresses below:
                </Typography>
                
                {/* Show current home address if it exists */}
                {client?.homeAddress?.line1 && (
                  <Box mb={2} p={2} sx={{ backgroundColor: 'rgba(25, 118, 210, 0.08)', borderRadius: 1, border: '1px solid rgba(25, 118, 210, 0.2)' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
                      Current Address
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      {client.homeAddress.line1}
                      {client.homeAddress.line2 && `, ${client.homeAddress.line2}`}
                      <br />
                      {client.homeAddress.city}, {client.homeAddress.postcode}
                    </Typography>
                  </Box>
                )}
                
                {/* Address Overlap Warning */}
                {addressOverlap?.hasOverlaps && (
                  <Box 
                    mb={2} 
                    p={2} 
                    sx={{ 
                      backgroundColor: 'rgba(211, 47, 47, 0.08)', 
                      borderRadius: 1, 
                      border: '1px solid rgba(211, 47, 47, 0.3)' 
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#D32F2F' }}>
                      ⚠️ Date Overlap Detected
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      {addressOverlap.overlapMessage}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', mt: 0.5, display: 'block' }}>
                      Please ensure address dates don't overlap
                    </Typography>
                  </Box>
                )}
                
                {/* Address Coverage Indicator */}
                {addressCoverage && !addressOverlap?.hasOverlaps && (
                  <Box 
                    mb={2} 
                    p={2} 
                    sx={{ 
                      backgroundColor: addressCoverage.hasGaps ? 'rgba(237, 108, 2, 0.08)' : 'rgba(46, 125, 50, 0.08)', 
                      borderRadius: 1, 
                      border: addressCoverage.hasGaps ? '1px solid rgba(237, 108, 2, 0.3)' : '1px solid rgba(46, 125, 50, 0.3)' 
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: addressCoverage.hasGaps ? '#ED6C02' : '#2E7D32' }}>
                      Address History Coverage
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      {addressCoverage.coverageText}
                    </Typography>
                    {addressCoverage.hasGaps && (
                      <Typography variant="caption" sx={{ color: '#666', mt: 0.5, display: 'block' }}>
                        Please add previous addresses to reach 5 years of history
                      </Typography>
                    )}
                  </Box>
                )}
                
                <Typography variant="body2" sx={{ mb: 2, color: '#555', fontWeight: 600 }}>
                  Previous Addresses:
                </Typography>
            {addressHistory.map((addr, index) => (
              <Box key={index} mb={2} p={2} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address Line 1"
                      value={addr.address.line1}
                      onChange={(e) => {
                        const newHistory = [...addressHistory];
                        newHistory[index].address.line1 = e.target.value;
                        setAddressHistory(newHistory);
                      }}
                      required
                      error={!addr.address.line1}
                      helperText={!addr.address.line1 ? 'Address Line 1 is required' : ''}
                      sx={{
                        backgroundColor: 'white',
                        '& .MuiInputBase-input': {
                          color: 'rgba(0, 0, 0, 0.87)',
                          fontWeight: 400
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(0, 0, 0, 0.6)'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City/Town"
                      value={addr.address.city}
                      onChange={(e) => {
                        const newHistory = [...addressHistory];
                        newHistory[index].address.city = e.target.value;
                        setAddressHistory(newHistory);
                      }}
                      required
                      error={!addr.address.city}
                      helperText={!addr.address.city ? 'City/Town is required' : ''}
                      sx={{
                        backgroundColor: 'white',
                        '& .MuiInputBase-input': {
                          color: 'rgba(0, 0, 0, 0.87)',
                          fontWeight: 400
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(0, 0, 0, 0.6)'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Postcode"
                      value={addr.address.postcode}
                      onChange={(e) => {
                        const newHistory = [...addressHistory];
                        newHistory[index].address.postcode = e.target.value.toUpperCase();
                        setAddressHistory(newHistory);
                      }}
                      required
                      error={!addr.address.postcode}
                      helperText={!addr.address.postcode ? 'Postcode is required' : ''}
                      sx={{
                        backgroundColor: 'white',
                        '& .MuiInputBase-input': {
                          color: 'rgba(0, 0, 0, 0.87)',
                          fontWeight: 400
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(0, 0, 0, 0.6)'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Moved In Date"
                        value={addr.movedInDate ? new Date(addr.movedInDate) : null}
                        onChange={(newValue) => {
                          const newHistory = [...addressHistory];
                          newHistory[index].movedInDate = newValue ? newValue.toISOString() : '';
                          setAddressHistory(newHistory);
                        }}
                        format="dd/MM/yyyy"
                        maxDate={new Date()}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            error: !addr.movedInDate,
                            helperText: !addr.movedInDate ? 'Moved In Date is required' : '',
                            sx: {
                              backgroundColor: 'white',
                              '& .MuiInputBase-input': {
                                color: 'rgba(0, 0, 0, 0.87)',
                                fontWeight: 400
                              },
                              '& .MuiInputLabel-root': {
                                color: 'rgba(0, 0, 0, 0.6)'
                              },
                              '& .MuiIconButton-root': {
                                color: 'rgba(0, 0, 0, 0.54)'
                              }
                            }
                          },
                          popper: {
                            sx: {
                              '& .MuiPaper-root': {
                                backgroundColor: 'white',
                                color: 'rgba(0, 0, 0, 0.87)'
                              },
                              '& .MuiPickersCalendarHeader-label': {
                                color: 'rgba(0, 0, 0, 0.87)'
                              },
                              '& .MuiPickersCalendarHeader-switchViewButton': {
                                color: 'rgba(0, 0, 0, 0.54)'
                              },
                              '& .MuiPickersArrowSwitcher-button': {
                                color: 'rgba(0, 0, 0, 0.54)'
                              },
                              '& .MuiDayCalendar-weekDayLabel': {
                                color: 'rgba(0, 0, 0, 0.6)'
                              },
                              '& .MuiPickersDay-root': {
                                color: 'rgba(0, 0, 0, 0.87)'
                              },
                              '& .MuiPickersYear-yearButton': {
                                color: 'rgba(0, 0, 0, 0.87)'
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Moved Out Date"
                        value={addr.movedOutDate ? new Date(addr.movedOutDate) : null}
                        onChange={(newValue) => {
                          const newHistory = [...addressHistory];
                          newHistory[index].movedOutDate = newValue ? newValue.toISOString() : '';
                          setAddressHistory(newHistory);
                        }}
                        format="dd/MM/yyyy"
                        maxDate={new Date()}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: {
                              backgroundColor: 'white',
                              '& .MuiInputBase-input': {
                                color: 'rgba(0, 0, 0, 0.87)',
                                fontWeight: 400
                              },
                              '& .MuiInputLabel-root': {
                                color: 'rgba(0, 0, 0, 0.6)'
                              },
                              '& .MuiIconButton-root': {
                                color: 'rgba(0, 0, 0, 0.54)'
                              }
                            }
                          },
                          popper: {
                            sx: {
                              '& .MuiPaper-root': {
                                backgroundColor: 'white',
                                color: 'rgba(0, 0, 0, 0.87)'
                              },
                              '& .MuiPickersCalendarHeader-label': {
                                color: 'rgba(0, 0, 0, 0.87)'
                              },
                              '& .MuiPickersCalendarHeader-switchViewButton': {
                                color: 'rgba(0, 0, 0, 0.54)'
                              },
                              '& .MuiPickersArrowSwitcher-button': {
                                color: 'rgba(0, 0, 0, 0.54)'
                              },
                              '& .MuiDayCalendar-weekDayLabel': {
                                color: 'rgba(0, 0, 0, 0.6)'
                              },
                              '& .MuiPickersDay-root': {
                                color: 'rgba(0, 0, 0, 0.87)'
                              },
                              '& .MuiPickersYear-yearButton': {
                                color: 'rgba(0, 0, 0, 0.87)'
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        const newHistory = addressHistory.filter((_, i) => i !== index);
                        setAddressHistory(newHistory);
                      }}
                    >
                      Remove Address
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Button
              variant="outlined"
              onClick={() => {
                setAddressHistory([
                  ...addressHistory,
                  {
                    address: { line1: '', line2: '', city: '', county: '', postcode: '' },
                    movedInDate: '',
                    movedOutDate: ''
                  }
                ]);
              }}
            >
              Add Previous Address
            </Button>
              </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, p: 2 }}>
          {/* Show validation warnings */}
          {showAdditionalInfo && (
            <>
              {addressHistory.length > 0 && addressHistory.some(addr => 
                !addr.address.line1 || !addr.address.city || !addr.address.postcode || !addr.movedInDate
              ) && (
                <Box sx={{ 
                  backgroundColor: 'rgba(237, 108, 2, 0.1)', 
                  border: '1px solid rgba(237, 108, 2, 0.3)',
                  borderRadius: 1,
                  p: 1.5,
                  mb: 1
                }}>
                  <Typography variant="body2" sx={{ color: '#ED6C02', fontWeight: 600 }}>
                    ⚠️ Please complete all required fields in the address history
                  </Typography>
                </Box>
              )}
              {(addressCoverage?.hasGaps ?? false) && addressCoverage && (
                <Box sx={{ 
                  backgroundColor: 'rgba(237, 108, 2, 0.1)', 
                  border: '1px solid rgba(237, 108, 2, 0.3)',
                  borderRadius: 1,
                  p: 1.5,
                  mb: 1
                }}>
                  <Typography variant="body2" sx={{ color: '#ED6C02', fontWeight: 600 }}>
                    ⚠️ {addressCoverage.coverageText}
                  </Typography>
                </Box>
              )}
              {(addressOverlap?.hasOverlaps ?? false) && addressOverlap && (
                <Box sx={{ 
                  backgroundColor: 'rgba(211, 47, 47, 0.1)', 
                  border: '1px solid rgba(211, 47, 47, 0.3)',
                  borderRadius: 1,
                  p: 1.5,
                  mb: 1
                }}>
                  <Typography variant="body2" sx={{ color: '#D32F2F', fontWeight: 600 }}>
                    ❌ {addressOverlap.overlapMessage}
                  </Typography>
                </Box>
              )}
            </>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => setShowAdditionalInfoDialog(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button 
            onClick={handleAcceptSubmit} 
            variant="contained" 
            color="success"
            disabled={
              submitting || 
              !name.trim() ||
              !position.trim() ||
              !signatureConfirmed ||
              (showAdditionalInfo && (
                !dateOfBirth || 
                !homeAddressLine1 || 
                !homeCity ||
                !homePostcode ||
                !homeMovedInDate ||
                !isAtLeast18(dateOfBirth) ||
                (addressCoverage?.hasGaps ?? false) ||
                (addressOverlap?.hasOverlaps ?? false) ||
                // Validate all address history entries have required fields
                (addressHistory.length > 0 && addressHistory.some(addr => 
                  !addr.address.line1 || 
                  !addr.address.city || 
                  !addr.address.postcode || 
                  !addr.movedInDate
                ))
              ))
            }
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LOAPage;
